import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Feature } from '@igo2/geo';
import { Constantes } from 'src/app/shared/constantes';
import { RechercheGdfService } from 'src/app/shared/services/recherche-gdf.service';
import { and, equalTo, or } from 'ol/format/filter';
import { SIGTRechercheGdfBaseComponent } from '../_sigt-recherche-gdf.base.component';
import { EntityTableTemplate } from '@igo2/common/entity';
import { LanguageService } from '@igo2/core/language';
import { MessageService } from '@igo2/core/message';
import { MatFormFieldModule } from '@angular/material/form-field';
import { OnlyDigitAcceptedDirective } from 'src/app/shared/directives/only-digit-accepted.directive';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import * as olstyle from 'ol/style';
import IsLike from 'ol/format/filter/IsLike';
import Filter from 'ol/format/filter/Filter';

export type EntityKey = string | number;
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import {  Fin, LocalisationDroit, MrcDroit, MunicipaliteDroit,
  RegionAdministrativeDroit, StatutDroit, TypeDroit } from 'src/app/shared/models/droit-gdf';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { FilterableMatSelectDirective } from 'src/app/shared/directives/filterable-mat-select.directive';

@Component({
    selector: 'app-intervenant-gdf',
    templateUrl: './intervenant-gdf.component.html',
    styleUrls: ['./intervenant-gdf.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        FilterableMatSelectDirective,
        MatButtonModule,
        OnlyDigitAcceptedDirective
    ]
})
export class IntervenantGdfComponent extends SIGTRechercheGdfBaseComponent implements OnInit {
  propertyKeyAndTitle: { [key: string]: string };
  template: EntityTableTemplate;

  form: UntypedFormGroup;
  couleurObligation = "var(--mat-form-field-filled-error-label-text-color)";
  visibilite = false;
  public typeDroitListeDeroulante: TypeDroit[] = [];
  public finsListeDeroulante: Fin[] = [];
  public statutListeDeroulante: StatutDroit[] = [];
  public listeLocalisations: any[] = [];
  public regionsAdministrativeListeDeroulante: RegionAdministrativeDroit[] = [];
  public mrcListeDeroulante: MrcDroit[] = [];
  public municipaliteListeDeroulante: MunicipaliteDroit[] = [];
  #configService = inject(ZzzConfigService);
  #valeurStatutDefaut: string = 'A';

  get urlBaseApi(): string {
    return this.#configService.get().authApiEndPoint + "/sigtapi";
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    languageService: LanguageService,
    rechercherGdfService: RechercheGdfService,
    httpClient: HttpClient,
    cdRef: ChangeDetectorRef,
    messageService: MessageService,
  ) {
    super(rechercherGdfService, httpClient, messageService, languageService, cdRef);
    this.propertyKeyAndTitle = this.rechercherGdfService.getPropertyKeyAndTitle('intervenantGdf');
    this.template =  this.rechercherGdfService.getEntityTableTemplate('intervenantGdf');
  }

  ngOnInit(): void {
    this.style = new olstyle.Style({
      stroke: new olstyle.Stroke({
        width: 1,
        color: [0, 0, 0, 1]
      }),
      fill: new olstyle.Fill({
        color: [11, 7, 240, 1]
      }),
      image: new olstyle.Circle({
        radius: 2,
        stroke: new olstyle.Stroke({
          width: 1,
          color: [0, 0, 0, 1]
        }),
        fill: new olstyle.Fill({
          color: [11, 7, 240, 1]
        })
      })
    });
    this.getLayer("Intervenant");
    this.form = this.formBuilder.group({
      nom: ['', [this.validerNonEspaceSeul]],
      prenom: ['', [this.validerNonEspaceSeul]],
      raisonSociale: ['', [this.validerNonEspaceSeul]],
      codePostal: ['', [this.validerNonEspaceSeul]],
      numeroTelephone: ['', [this.validerNonEspaceSeul, Validators.pattern(/^[0-9]*$/)]],
      typeDroit: [""],
      fin: [""],
      statutDroit: [""],
      municipalite: [""],
      mrc: [""],
      regionAdministrative: [""]
     }, { validators: [this.validation.bind(this)] });

    ["nom", "prenom", "raisonSociale", "codePostal", "numeroTelephone"].forEach(controlName => {
      this.form.controls[controlName].valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((searchTerm: string) => {
            if (searchTerm) {
              this.chargerFins();
            }
            return searchTerm;
          })
        ).subscribe(_ => { });
    });

    this.messageApplication.grilleVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messageApplication.erreurCouche = Constantes.MessageApplicatif.CoucheIndisponible_16T;

    this.chargerTypesDroits();
    this.chargerFins();
    this.chargerStatus();
    this.chargerLocalisations();
  }

  validation(control: FormGroup): any {
    const nom = control.controls["nom"].value;
    const prenom = control.controls["prenom"].value;
    const raisonSociale = control.controls["raisonSociale"].value;
    const codePostal = control.controls["codePostal"].value;
    const numeroTelephone = control.controls["numeroTelephone"].value;
    const fin = control.controls["fin"].value;
    const typeDroit = control.controls["typeDroit"].value;
    const municipalite = control.controls["municipalite"].value;
    const mrc = control.controls["mrc"].value;
    const regionAdministrative = control.controls["regionAdministrative"].value;

    //Au moins un des champs selectionné pour être valide, mais pas juste le statutDroit.
    if ((nom !== null && nom.trim() !== "") ||
        (prenom !== null && prenom.trim() !== "") ||
        (raisonSociale !== null && raisonSociale.trim() !== "") ||
        (codePostal !== null && codePostal.trim() !== "") ||
        (numeroTelephone !== null && numeroTelephone.trim() !== "") ||
        typeDroit?.length > 0 || fin?.length > 0 || municipalite?.length > 0 || mrc?.length > 0 || regionAdministrative?.length > 0) {
        return null;
    }
    return { notValid: true };
  }

  initialiserChamps(): void {
    this.form.reset();
    this.chargerTypesDroits();
    this.chargerFins();
    this.chargerStatus();
    this.chargerLocalisations();
    this.visibilite = true;
  }

  chargerTypesDroits() {
    this.httpClient.post<{ listeTypeDroit: TypeDroit[] }>(this.urlBaseApi + "/gdf/intervenant/types-droit",
      {
        "valeur": {
          "nom": this.form.get("nom").value,
          "prenom": this.form.get("prenom").value,
          "raisonSociale": this.form.get("raisonSociale").value,
          "codePostal": this.form.get("codePostal").value,
          "telephone": this.form.get("numeroTelephone").value
        }
      }
    ).subscribe({
      next: reponse => {
        this.typeDroitListeDeroulante = reponse.listeTypeDroit.sort((a, b) =>
          a.descriptionTypeDroit.localeCompare(b.descriptionTypeDroit));
      },
      error: error => {
        this.messageService.error("ERREUR APPEL gdf/intervenant/types-droit");
        console.log(error);
      }
    });
  }

  chargerFins() {
    this.loadFins().subscribe();
  }

  loadFins(): Observable<Fin[]> {
    return this.httpClient.post<{ listeFins: Fin[] }>(this.urlBaseApi + '/gdf/intervenant/fins',
      {
        "valeur": {
          "nom": this.form.get("nom").value,
          "prenom": this.form.get("prenom").value,
          "raisonSociale": this.form.get("raisonSociale").value,
          "codePostal": this.form.get("codePostal").value,
          "telephone": this.form.get("numeroTelephone").value,
          "criteresTypeDroit": this.form.get("typeDroit").value || []
        }
      }
    ).pipe(
      tap(reponse => {
        let selectionFinEncoreValide;
        if (this.form.get("fin").value) {
          selectionFinEncoreValide = Object.values(this.form.get("fin").value).filter(x => {
            if (reponse.listeFins.some(item => item.codeFin === x))
              return x;
          });
        }
        let finsAvecNo = reponse.listeFins.map(item => ({
          codeFin: item.codeFin,
          descriptionFin: item.codeFin + ' - ' + item.descriptionFin
        }));
        this.finsListeDeroulante = finsAvecNo.sort((a, b) => Number(a.codeFin) - Number(b.codeFin));
        this.form.get("fin").setValue(selectionFinEncoreValide);
      }),
      map(reponse => reponse.listeFins),
      catchError(error => {
        this.messageService.error("ERREUR APPEL gdf/intervenant/fins");
        console.log(error);
        return of([]);
      })
    );
  }

  chargerStatus() {
    //Les status sont chargés une seule fois
    this.httpClient.get<{ listeStatutDroit: StatutDroit[] }>(this.urlBaseApi + "/gdf/intervenant/statuts")
      .subscribe({
        next: reponse => {
          this.statutListeDeroulante = reponse.listeStatutDroit.sort((a, b) => a.nomStatut.localeCompare(b.nomStatut));
          this.defautStatut();
        },
        error: error => {
          this.messageService.error("ERREUR APPEL gdf/intervenant/statuts");
          console.log(error);
        }
      });
  }

  defautStatut() {
    if (this.form.get('statutDroit').value == null || String(this.form.get('statutDroit').value) === '') {
      if (this.statutListeDeroulante.filter(statut => statut.codeStatut === this.#valeurStatutDefaut).length > 0) {
        this.form.get("statutDroit").setValue([this.#valeurStatutDefaut]);
      }
    }
  }

  chargerLocalisations() {
    this.httpClient.post<{ listeLocalisations: LocalisationDroit[] }>(this.urlBaseApi + "/gdf/intervenant/localisations",
      {
        "valeur": {
          "nom": this.form.get("nom").value,
          "prenom": this.form.get("prenom").value,
          "raisonSociale": this.form.get("raisonSociale").value,
          "codePostal": this.form.get("codePostal").value,
          "telephone": this.form.get("numeroTelephone").value,
          "criteresTypeDroit": this.form.get("typeDroit").value || [],
          "criteresFin": this.form.get("fin").value || [],
          "criteresStatut": this.form.get("statutDroit").value || []
        }
      }
    ).subscribe({
      next: reponse => {
        this.listeLocalisations = reponse.listeLocalisations;

        let distinctRegions = reponse.listeLocalisations.reduce((prev, el) => {
          if (prev.some(o => o.codeRegion === el.codeRegion && o.nomRegion === el.nomRegion))
            return prev;
          return [...prev, { codeRegion: el.codeRegion, nomRegion: el.nomRegion }];
        }, []
        ).sort((a, b) => Number(a.codeRegion) - Number(b.codeRegion));

        distinctRegions = distinctRegions.map(({ codeRegion, nomRegion }) => (
          {
            codeRegion,
            nomRegion: (codeRegion.length === 2 ? codeRegion : "0" + codeRegion) + ' - ' + nomRegion
          }
        ));
        this.regionsAdministrativeListeDeroulante = distinctRegions;

        this.mrcListeDeroulante = reponse.listeLocalisations
        .filter(el => el.codeMrc !== '-') //SIGT-1568        
        .reduce((prev, el) => {
          if (prev.some(o => o.codeMrc === el.codeMrc && o.nomMrc === el.nomMrc))
            return prev;
          return [...prev, { codeMrc: el.codeMrc, nomMrc: el.nomMrc }];
        }, []
        ).sort((a, b) => a.nomMrc.localeCompare(b.nomMrc));

        this.municipaliteListeDeroulante = reponse.listeLocalisations
          .map(({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
          .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));

        let selectionRegionsEncoreValide;
        if (this.form.get("regionAdministrative").value) {
          selectionRegionsEncoreValide = Object.values(this.form.get("regionAdministrative").value).filter(x => {
            if (this.regionsAdministrativeListeDeroulante.some(item => item.codeRegion === x))
              return x;
          });
        }
        this.form.get("regionAdministrative").setValue(selectionRegionsEncoreValide);

        let selectionMrcEncoreValide;
        if (this.form.get("mrc").value) {
          selectionMrcEncoreValide = Object.values(this.form.get("mrc").value).filter(x => {
            if (this.mrcListeDeroulante.some(item => item.codeMrc === x))
              return x;
          });
        }
        this.form.get("mrc").setValue(selectionMrcEncoreValide);

        let selectionMuniEncoreValide;
        if (this.form.get("municipalite").value) {
          selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
            if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x))
              return x;
          });
        }
        this.form.get("municipalite").setValue(selectionMuniEncoreValide);
      },
      error: error => {
        this.messageService.error("ERREUR APPEL gdf/droit/localisations");
        console.log(error);
      }
    });
  }

  selectTypeDroitChanged() {
     this.loadFins().subscribe({
       next: _ => {
         this.chargerLocalisations();
       }
     });
  }

  selectFinChanged() {
    this.chargerLocalisations();
  }

  selectStatutChanged() {
    this.chargerLocalisations();
  }

  selectMrcChange() {
    let localisationFiltrer = this.listeLocalisations;
    if (this.form.get("mrc").value && (Object.values(this.form.get("mrc").value)).length > 0) {
      localisationFiltrer = this.listeLocalisations.filter(x => {
        if ((Object.values(this.form.get("mrc").value)).some(item => item === x.codeMrc))
          return x;
      });
    }

    if (this.form.get("regionAdministrative").value && (Object.values(this.form.get("regionAdministrative").value)).length > 0) {
      localisationFiltrer = localisationFiltrer.filter(x => {
        if ((Object.values(this.form.get("regionAdministrative").value)).some(item => item === x.codeRegion))
          return x;
      });
    }

    this.municipaliteListeDeroulante = localisationFiltrer.map(
      ({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
      .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));

    let selectionMuniEncoreValide;
    if (this.form.get("municipalite").value) {
      selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
        if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x))
          return x;
      });
    }
    this.form.get("municipalite").setValue(selectionMuniEncoreValide);
  }

  selectRegionChange() {
    let localisationFiltrerRegion = this.listeLocalisations;
    if (this.form.get("regionAdministrative").value && (Object.values(this.form.get("regionAdministrative").value)).length > 0) {
      localisationFiltrerRegion = this.listeLocalisations.filter(x => {
        if ((Object.values(this.form.get("regionAdministrative").value)).some(item => item === x.codeRegion))
          return x;
      });
    }
    this.mrcListeDeroulante = localisationFiltrerRegion
    .filter(el => el.codeMrc !== '-') //SIGT-1568    
    .reduce((prev, el) => {
      if (prev.some(o => o.codeMrc === el.codeMrc && o.nomMrc === el.nomMrc))
        return prev;
      return [...prev, { codeMrc: el.codeMrc, nomMrc: el.nomMrc }];
    }, []
    ).sort((a, b) => a.nomMrc.localeCompare(b.nomMrc));

    if (this.form.get("mrc").value && (Object.values(this.form.get("mrc").value)).length > 0) {
      let selectionMrcEncoreValide;
      selectionMrcEncoreValide = Object.values(this.form.get("mrc").value).filter(x => {
        if (this.mrcListeDeroulante.some(item => item.codeMrc === x)) return x;
      });
      this.form.get("mrc").setValue(selectionMrcEncoreValide);
      this.selectMrcChange();
    }
    else {
      this.municipaliteListeDeroulante = localisationFiltrerRegion.map(
        ({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
        .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));
      let selectionMuniEncoreValide;
      if (this.form.get("municipalite").value) {
        selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
          if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x)) return x;
        });
      }
      this.form.get("municipalite").setValue(selectionMuniEncoreValide);
    }
  }

  buildFilter(): Filter {
    const nom = this.form.get('nom').value;
    const prenom = this.form.get('prenom').value;
    const raisonSociale = this.form.get('raisonSociale').value;
    const codePostal = this.form.get('codePostal').value;
    const numeroTelephone = this.form.get('numeroTelephone').value;
    const typeDroit = this.form.get("typeDroit").value;
    const fin = this.form.get("fin").value;
    const statut = this.form.get("statutDroit").value;
    const municipalite = this.form.get("municipalite").value;
    const mrc = this.form.get("mrc").value;
    const regionAdministrative = this.form.get("regionAdministrative").value;

    let filter: Filter;
    let subFilters: Filter[] = [];

    if (nom !== '' && nom !== null) {
      subFilters.push(new IsLike('NOM_1', '%' + nom + '%', undefined, undefined, undefined, false));
    }
    if (prenom !== '' && prenom !== null) {
      subFilters.push(new IsLike('NOM_2', '%' + prenom + '%', undefined, undefined, undefined, false));
    }
    if (raisonSociale !== '' && raisonSociale !== null) {
      subFilters.push(new IsLike('NOM_3', '%' + raisonSociale + '%', undefined, undefined, undefined, false));
    }
    if (codePostal !== '' && codePostal !== null) {
      subFilters.push(new IsLike('ADR_CO_POS', '%' + codePostal + '%', undefined, undefined, undefined, false));
    }
    if (numeroTelephone !== '' && numeroTelephone !== null) {
      subFilters.push(
        or(
          new IsLike('TEL_ENT', '%' + numeroTelephone + '%', undefined, undefined, undefined, false),
          new IsLike('TEL_AUT', '%' + numeroTelephone + '%', undefined, undefined, undefined, false)
        )
      );
    }
    if (typeDroit !== null && typeDroit !== undefined && typeDroit.length > 0)
    {
      let eqFilters = typeDroit.map(t => equalTo('TDR_TY', t));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    if (fin !== null && fin !== undefined && fin.length > 0) {
      let eqFilters = fin.map(f => equalTo('FIN_NO', Number(f)));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    if (statut !== null && statut !== undefined && statut.length > 0) {
      let eqFilters = statut.map(f => equalTo('DRT_STA', f));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }

    if (municipalite !== null && municipalite !== undefined && municipalite.length > 0) {
      let eqFilters = municipalite.map(m => equalTo('MUN_NO', m));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    else if (mrc !== null && mrc !== undefined && mrc.length > 0) {
      let eqFilters = mrc.map(m => equalTo('MRC_NO', m));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    else if (regionAdministrative !== null && regionAdministrative !== undefined && regionAdministrative.length > 0) {
      let eqFilters = regionAdministrative.map(r => equalTo('REA_NO', r));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }

    filter = subFilters.length > 1 ? and(...subFilters) : subFilters[0];
    return filter;
  }

  formaterChampsFeaturePourResultatRecherche(features: Feature[]): Feature[] {
    return this.rechercherGdfService.formatFeaturesForTable('intervenantGdf', features);
  }
}
